import React, {useState, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import CloseIcon from '@mui/icons-material/Close';
import Slide from '@mui/material/Slide';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';

import AutocompletePlace from './AutoCompletePlace';
import {BluePillButton} from '../../common/components/BluePillButton';
import {updateStudent} from '../../redux/slices/builder';

import baseStyles from '../../css/Base.module.css';

const GRADE_LEVELS = [
  'Kindergarten',
  'First grade',
  'Second grade',
  'Third grade',
  'Fourth grade',
  'Fifth grade',
  'Sixth grade',
  'Seventh grade',
  'Eighth grade',
  'Ninth grade',
  'Tenth grade',
  'Eleventh grade',
  'Twelfth grade',
];
const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

export default function ConfirmStudentDialog({isOpen, handleClose, student, handleConfirmStudent}) {
  const dispatch = useDispatch();

  const [isSaving, toggleIsSaving] = useState(false);
  const [school, setSchool] = useState(null);
  const [gradeLevel, setGradeLevel] = useState('');

  useEffect(() => {
    if (student) {
      setSchool(student.school);
      setGradeLevel(student.gradeLevel);
    }
  }, [student]);


  function isSaveButtonEnabled() {
    return school && !!gradeLevel && !isSaving;
  }

  async function handleClickSave() {
    toggleIsSaving(true);

    const studentPayload = {
      school,
      gradeLevel,
    };
    const updatedStudent = await dispatch(updateStudent(student.id, studentPayload));

    toggleIsSaving(false);
    handleConfirmStudent(updatedStudent);
  }

  function renderDialogContent() {
    return (
      <>
        {isSaving && (
          <div className={baseStyles['progress-container']}>
            <CircularProgress />
          </div>
        )}

        {!isSaving && (
          <>
            <div className="p-20">
              <Typography variant="subtitle1" className="clr-dark-blue fnt-bold">
                Confirm student school and grade
              </Typography>
              <Grid container>
                <Grid item xs={8}>
                  <Grid item md={6} xs={12} className="m-t-10">
                    <AutocompletePlace
                      onSelect={setSchool}
                      initialValue={school}
                      inputLabel="School"
                      inputVariant="outlined"
                      isRequired
                    />
                  </Grid>
                  <Grid item md={6} xs={12} className="m-t-10">
                    <FormControl fullWidth required>
                      <InputLabel>Grade level</InputLabel>
                      <Select
                        label="Grade level"
                        value={gradeLevel}
                        onChange={(event) => setGradeLevel(event.target.value)}
                      >
                        {GRADE_LEVELS.map((gradeLevel) => (
                          <MenuItem key={gradeLevel} value={gradeLevel}>
                            {gradeLevel}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            </div>
            <AppBar position="fixed" className="bg-gray-100" sx={{top: 'auto', bottom: 0}}>
              <Toolbar>
                <Grid container direction="row" justifyContent="center" alignItems="center">
                  <Button className={baseStyles['btn-pill']} onClick={handleClose}>
                    Cancel
                  </Button>
                  <BluePillButton variant="outlined" disabled={!isSaveButtonEnabled()} onClick={handleClickSave}>
                    {isSaving ? <CircularProgress size={25} /> : 'Confirm'}
                  </BluePillButton>
                </Grid>
              </Toolbar>
            </AppBar>
            <Toolbar /> {/* Makes AppBar at the bottom of the page */}
          </>
        )}
      </>
    );
  }

  return (
    <Dialog fullScreen open={isOpen} onClose={handleClose} TransitionComponent={Transition}>
      <AppBar sx={{position: 'relative'}} className="bg-dark-blue">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
          <Typography sx={{ml: 2, flex: 1}} variant="h6" component="div">
            Confirm {student?.firstName}&apos;s Information
          </Typography>
        </Toolbar>
      </AppBar>

      {renderDialogContent()}
    </Dialog>
  );
}
